import React, { createRef } from 'react'
import Configurator from '../../../configurator'
import classnames from 'classnames'
import isIn from 'validator/lib/isIn'
import { isWebView } from '../../../admin/actions'
import store from '../../../admin/store'

import Button from '../../../front/components/shared/sn-button'
import Input from '../../../front/components/shared/sn-input'

import {
    i18n,
    withRouterConnectWrapper,
} from '../../../validator'
import {
    getBrowserHistoryFilters,
    searchParams,
} from '../../../front/components/filters/search-params'

import { RESPONSIVE_WIDTH } from '../../../consts'
import Loader from '../../../front/components/shared/loader'

/**
 * Routes where will input not will be rendered
 *
 * @type {string[]}
 */
const HIDDEN_ROUTES = [
    '/sportnections',
]

@i18n()
class SearchInput extends Configurator {
    state = {
        name: this.props.name,
    }

    constructor(props) {
        super(props)

        this.searchInputRef = createRef()
    }

    componentDidMount() {
        const searchParamsObject = searchParams()
        const name = searchParamsObject?.name?.[0] || ''

        this.changeSearch(name)

        store.dispatch({
            type: 'FILTERS_SET_NAME',
            value: name,
        })
    }

    /**
     * Check if device is iOS Safari
     *
     * Workaround fix to automatic redirect into entities due on focus on search input
     *
     * @return {boolean}
     */
    isiOSSafari = () => {
        const userAgent = window.navigator.userAgent

        return /iP(ad|od|hone)/i.test(userAgent) &&
            /WebKit/i.test(userAgent) &&
            !(/(CriOS|FxiOS|OPiOS|mercury)/i.test(userAgent))
    }

    /**
     * Handler for change input focus
     *
     * @param {boolean} hasFocus
     */
    inputFocusChanged = hasFocus => {
        const {
            onFocusChanged,
            onFocusRedirectToEntities,
        } = this.props

        if (typeof onFocusChanged === 'function') {
            onFocusChanged(hasFocus)
        }

        if (hasFocus && onFocusRedirectToEntities && (!this.isiOSSafari() && !isWebView())) {
            this.redirectToEntities()
                .then(() => this?.searchInputRef?.current?.focus())
        }
    }

    /**
     * Check if is filtering
     *
     * @return {string | boolean}
     */
    isFiltering = () => {
        const { name } = this.state

        return name && name !== ''
    }

    /**
     * Decline search
     */
    clearSearch = () => {
        this.setState({ name: '' })

        store.dispatch({
            type: 'FILTERS_RESET',
        })
    }

    /**
     * On search change
     *
     * @param {string} value
     * @return {void}
     */
    changeSearch = value => this.setState({ name: value })

    /**
     * Search handler
     *
     * @param {SyncEvent} event
     */
    searchHandler = event => {
        event.preventDefault()

        const { name } = this.state

        const searchInput = this.searchInputRef.current

        if (searchInput) {
            searchInput.blur()
        }

        if (!this.isFiltering()) {
            this.clearSearch()
            return
        }

        this.redirectToEntities()
            .then(() => {
                store.dispatch({
                    type: 'FILTERS_SET_NAME',
                    value: name,
                })
            })
    }

    /**
     * Redirect into entities
     *
     * @return {Promise}
     */
    redirectToEntities = () => {
        const {
            history,
            isWhitelistedRoute,
        } = this.props

        if (!isWhitelistedRoute) {
            return new Promise(resolve => {
                const queryParams = getBrowserHistoryFilters([{ name: name }])
                history.push(`/${this.config.REACT_APP_TRANSLATION_ROUTES.entities}?${queryParams}`)
                resolve()
            })
        }

        return new Promise(resolve => resolve())
    }

    /**
     * Render
     *
     * @return {*}
     */
    render() {
        const isFiltering = this.isFiltering()
        const { name } = this.state
        const {
            busyEntities,
            history,
            t,
        } = this.props
        const isResponsive = this.config.windowWidth <= RESPONSIVE_WIDTH

        if (isIn(history.location.pathname, HIDDEN_ROUTES)) {
            return null
        }

        if (busyEntities) {
            return (
                <div className="search-input flex-shrink-0">
                    <Input
                        disabled={true}
                        singleInput
                        type="search"
                        className="active"
                        placeholder={`${t('search')}...`}
                        value={name}
                    />
                    <div className="search-input__buttons">
                        <Button className="search-input__loading">
                            <Loader />
                        </Button>
                    </div>
                </div>
            )
        }

        return (
            <form
                action=""
                className="search-input flex-shrink-0"
                onSubmit={this.searchHandler}
            >
                <Input
                    autoFocus={isResponsive}
                    singleInput
                    id="main-search-input"
                    type="search"
                    inputRef={this.searchInputRef}
                    name="search"
                    className={classnames({
                        'active': isFiltering,
                    })}
                    placeholder={`${t('search')}...`}
                    value={name}
                    onChange={event => this.changeSearch(event.target.value)}
                    onFocus={() => this.inputFocusChanged(true)}
                    onBlur={() => this.inputFocusChanged(false)}
                />

                <div className="search-input__buttons">
                    <Button
                        className={classnames({
                            'search-input__cross': true,
                            'active': isFiltering,
                        })}
                        onClick={this.clearSearch}
                    />
                    <Button
                        type="submit"
                        className={classnames({
                            'search-input__search': true,
                            'active': isFiltering,
                        })}
                        onClick={(event) => {
                            if (!isFiltering) {
                                event.preventDefault()
                                this.searchInputRef.current.focus()
                            }
                        }}
                    />
                </div>
            </form>
        )
    }
}

export default withRouterConnectWrapper(state => ({
    name: state.filters.name,
    isWhitelistedRoute: state.filters.isWhitelistedRoute,
    busyEntities: state.ui.busySections.entities,
}))(SearchInput)
